/** Native scroll-snap carousel. Each editor instance owns and releases its listeners. */
class ICFeaturedProducts extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.track = this.querySelector('.ic-fp__track');
    if (!this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.cards = Array.from(this.track.children);
    this.controls = this.querySelector('.ic-fp__controls');
    this.dots = this.querySelector('.ic-fp__dots');
    this.status = this.querySelector('.ic-fp__status');
    this.previous = this.querySelector('[data-direction="previous"]');
    this.next = this.querySelector('[data-direction="next"]');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.page !== undefined) this.go(Number(button.dataset.page));
      else if (button.dataset.direction) this.go(this.page + (button.dataset.direction === 'next' ? 1 : -1));
    }, { signal });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !this.isCarousel) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.go(0);
      else if (event.key === 'End') this.go(this.pages.length - 1);
      else this.go(this.page + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
    }, { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }, { signal, passive: true });
    this.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest('.ic-fp__item');
      if (card && this.isCarousel) card.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
    }, { signal });
    this.observer = new ResizeObserver(() => this.layout());
    this.observer.observe(this.track);
    this.layout();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  layout() {
    this.isCarousel = getComputedStyle(this.track).overflowX === 'auto';
    const columns = Number(getComputedStyle(this.track).getPropertyValue('--ic-columns')) || 1;
    this.pages = [];
    for (let i = 0; i < this.cards.length; i += columns) this.pages.push(i);
    const enabled = this.isCarousel && this.track.scrollWidth > this.track.clientWidth + 1;
    this.controls.hidden = !enabled || (!this.previous && !this.next && !this.dots);
    this.track.tabIndex = enabled ? 0 : -1;
    if (this.dots && this.dots.children.length !== this.pages.length) {
      this.dots.replaceChildren(...this.pages.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ic-fp__dot';
        button.dataset.page = String(index);
        button.setAttribute('aria-label', this.pageLabel(index));
        button.setAttribute('aria-controls', this.track.id);
        return button;
      }));
    }
    this.update();
  }

  update() {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const track = this.track.getBoundingClientRect();
    const edge = rtl ? track.right : track.left;
    let distance = Infinity;
    this.page = 0;
    this.pages.forEach((index, page) => {
      const card = this.cards[index].getBoundingClientRect();
      const value = Math.abs((rtl ? card.right : card.left) - edge);
      if (value < distance) { distance = value; this.page = page; }
    });
    // The last page may contain fewer cards and cannot align to the start edge.
    if (Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2) this.page = this.pages.length - 1;
    if (this.previous) this.previous.disabled = this.page === 0;
    if (this.next) this.next.disabled = this.page >= this.pages.length - 1;
    this.dots?.querySelectorAll('button').forEach((button, page) => button.setAttribute('aria-current', String(page === this.page)));
    const text = this.pageLabel(this.page);
    if (this.status.textContent !== text) this.status.textContent = text;
  }

  pageLabel(page) {
    return `${(this.dataset.pageLabel || 'Page [page]').replace('[page]', String(page + 1))} / ${this.pages.length}`;
  }

  go(page) {
    if (!this.isCarousel) return;
    const index = this.pages[Math.max(0, Math.min(page, this.pages.length - 1))];
    const smooth = this.dataset.animations === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.cards[index]?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'nearest', inline: 'start' });
  }
}

if (!customElements.get('ic-featured-products')) customElements.define('ic-featured-products', ICFeaturedProducts);
